import type {
  Wallet,
  WalletOption,
  DynamicWalletKey,
} from '@stripe-internal/wallet-connect-wrapper';
import type {NetworkType, SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';
import {getNetworkTypeFromWalletKey} from 'src/v2/util/getWalletKey';

/**
 * Determines which network types are relevant for this session based on supported currencies
 */
const getRelevantNetworkTypes = (
  supportedCurrencies: SupportedCurrency[],
): NetworkType[] => {
  const supportedNetworks = [
    ...new Set(supportedCurrencies.map((currency) => currency.currencyNetwork)),
  ];

  const networkTypes: NetworkType[] = [];

  // Check if EVM networks are supported
  if (supportedNetworks.some((network) => EVM_NETWORKS.includes(network))) {
    networkTypes.push('evm');
  }

  // Check if Solana is supported
  if (supportedNetworks.includes('solana')) {
    networkTypes.push('solana');
  }

  return networkTypes;
};

/**
 * Determines the network type for a WalletOption based on its key or provider
 */
const getNetworkTypeForWalletOption = (
  walletOption: WalletOption,
): NetworkType[] => {
  if (walletOption.provider === 'WalletConnect' && walletOption.key) {
    // For WalletConnect, determine network type from the key
    return ['evm'];
  }

  return ['evm', 'solana'];
};

const getConnectedWalletTypes = (connectedWallets: Wallet[]): Set<string> => {
  const connectedTypes = new Set<string>();

  connectedWallets.forEach((wallet) => {
    const provider = wallet.type;
    const networkType = getNetworkTypeFromWalletKey(
      wallet.key as DynamicWalletKey,
    );

    // Create a unique key for provider + network combination
    const typeKey = `${provider}:${networkType}`;
    connectedTypes.add(typeKey);
  });

  return connectedTypes;
};

/**
 * Filters out wallet options that are already connected for all relevant network types
 * A wallet type is only hidden if ALL relevant networks for the session are already connected
 */
export const filterAvailableWalletOptions = (
  allWalletOptions: WalletOption[],
  connectedWallets: Wallet[],
  supportedCurrencies: SupportedCurrency[],
): WalletOption[] => {
  const relevantNetworkTypes = getRelevantNetworkTypes(supportedCurrencies);

  return allWalletOptions.filter((walletOption) => {
    const allSupportedNetworks = getNetworkTypeForWalletOption(walletOption);

    // Only consider networks that are supported by this session
    const relevantSupportedNetworks = allSupportedNetworks.filter((network) =>
      relevantNetworkTypes.includes(network),
    );

    // Skip wallets that don't support any relevant networks
    if (relevantSupportedNetworks.length === 0) {
      return false;
    }

    const connectedTypes = getConnectedWalletTypes(connectedWallets);

    // Check which support networks are already connected
    const connectedRelevantNetworks = relevantSupportedNetworks.filter(
      (networkType) =>
        connectedTypes.has(`${walletOption.provider}:${networkType}`),
    );

    // Hide only if ALL supported networks are connected
    const allRelevantNetworksConnected =
      connectedRelevantNetworks.length === relevantSupportedNetworks.length;

    return !allRelevantNetworksConnected;
  });
};
